import React from 'react';
import PropTypes from 'prop-types';
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
} from 'recharts';

import { Spacer } from '@zillow/constellation';

import GraphTitle from 'app/shared/components/Graphs/GraphTitle';
import GraphEmptyMessage from 'app/shared/components/Graphs/GraphEmptyMessage';
import SpinnerContainer from 'app/shared/components/SpinnerContainer';
import GraphConstants from 'app/shared/constants/GraphConstants';

import subMonths from 'date-fns/sub_months';
import format from 'date-fns/format';


const propTypes = {
    data: PropTypes.object.isRequired,
    isLoading: PropTypes.bool,
};

const defaultProps = {
    isLoading: false,
};

const GRAPH = GraphConstants.GRAPHS.REVIEW;
const TABS = GraphConstants.GRAPH_TABS;

const AVERAGE_DOMAIN = [0, 5];
const AUTO_DOMAIN = ['auto', 'auto'];
const AVERAGE_TICKS = [0, 1, 2, 3, 4, 5];

const formatAverage = num => (isNaN(num) ? 0 : Math.round(num * 10) / 10);

class ReviewGraph extends React.PureComponent {
    constructor(props) {
        super(props);
        this.state = {
            activeTab: TABS.AVERAGE,
        };
        this.handleTabClick = this.handleTabClick.bind(this);
    }

    handleTabClick(index) {
        this.setState({ activeTab: index === 0 ? TABS.AVERAGE : TABS.TOTAL });
    }

    renderGraph() {
        const { data } = this.props;
        const { activeTab } = this.state;
        const isAverage = activeTab === TABS.AVERAGE;
        const dataKey = isAverage ? 'Rating' : 'Reviews';

        const domain = isAverage ? AVERAGE_DOMAIN : AUTO_DOMAIN;
        const ticks = isAverage ? AVERAGE_TICKS : null;

        const mappedData = data.map((item, i) => {
            const dataPoint = {

                name: format(subMonths(new Date(), 11 - i), 'MMM YYYY'),
            };
            if (isAverage) {
                const average = formatAverage(item.stars / item.reviews);
                if (average) {
                    dataPoint[dataKey] = average;
                }
            } else {
                dataPoint[dataKey] = item.reviews;
            }
            return dataPoint;
        });
        return (
            <div>
                {data.isEmpty && <GraphEmptyMessage message="No reviews available" />}
                <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={mappedData} key={dataKey}>
                        <XAxis dataKey="name" />
                        <YAxis domain={domain} ticks={ticks} />
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <Tooltip />
                        <Line
                            dataKey={dataKey}
                            stroke={GraphConstants.COLORS.zillow.secondary}
                            dot={{ r: 5, fill: GraphConstants.COLORS.zillow.secondary }}
                            activeDot={{ r: 8 }}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        );
    }

    render() {
        const {
            isLoading,
            data,
        } = this.props;
        const { activeTab } = this.state;
        const isAverage = activeTab === TABS.AVERAGE;

        const configs = GRAPH[activeTab];
        let number = 0;
        const totalReviews = data.map(item => item.reviews).reduce((a, b) => a + b, 0);
        if (isAverage) {
            const totalStars = data.map(item => item.stars).reduce((a, b) => a + b, 0);
            number = formatAverage(totalStars / totalReviews);
        } else {
            number = totalReviews;
        }
        const displayNumber = (isLoading || number === '0.0' || number === '0') ? '-' : number;
        const title = `${configs.TITLE_PREFIX}: ${displayNumber}${activeTab === TABS.AVERAGE ? '/5' : ''}`;

        return (
            <Spacer marginY="md">
                <GraphTitle
                    title={title}
                    isLoading={isLoading}
                    showTabs={true}
                    onTabClick={this.handleTabClick}
                    activeIndex={configs.TAB_INDEX}
                    firstTabTitle="Ratings"
                    secondTabTitle="# of reviews"
                />
                {isLoading ? <SpinnerContainer /> : this.renderGraph()}
            </Spacer>
        );
    }
}

ReviewGraph.propTypes = propTypes;
ReviewGraph.defaultProps = defaultProps;

export default ReviewGraph;
